import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  PlusIcon,
  BookOpenIcon,
  UserIcon,
  CalendarIcon,
  CurrencyRupeeIcon,
  PencilSquareIcon,
  TrashIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../Common/LoadingSpinner';
import EnrollmentModal from './EnrollmentModal';
import toast from 'react-hot-toast';

const CourseList = () => {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('');
  const [pagination, setPagination] = useState({});
  const [enrolledCourseIds, setEnrolledCourseIds] = useState(new Set());

  const formatDuration = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m ${secs}s`;
};

  const categories = [
    'Computer Science', 'Mathematics', 'Physics', 'Chemistry',
    'Biology', 'English', 'History', 'Arts', 'Business', 'Other'
  ];

  const levels = ['Beginner', 'Intermediate', 'Advanced'];

  useEffect(() => {
    fetchCourses();
    if (user?.role === 'student') fetchStudentEnrollments();
  }, [searchTerm, selectedCategory, selectedLevel, user]);

  const fetchStudentEnrollments = async () => {
    try {
      const res = await axios.get(`/api/enrollments/student/${user._id}`);
      const ids = new Set(res.data.filter(e => e.status === 'enrolled').map(e => e.course._id));
      setEnrolledCourseIds(ids);
    } catch (err) {
      console.error('Failed to fetch student enrollments', err);
    }
  };

  const fetchCourses = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '12'
      });

      if (searchTerm) params.append('search', searchTerm);
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedLevel) params.append('level', selectedLevel);

      const response = await axios.get(`/api/courses?${params}`);
      setCourses(response.data.courses || response.data);
      setPagination(response.data.pagination || {});
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Failed to fetch courses');
    } finally {
      setLoading(false);
    }
  };

  const [showEnrollmentModal, setShowEnrollmentModal] = useState({ show: false, course: null });
  const [enrollmentLoading, setEnrollmentLoading] = useState(false);

  const handleEnrollClick = (course) => {
    setShowEnrollmentModal({ show: true, course });
  };

  const handleEnroll = async (useWallet = false) => {
    const course = showEnrollmentModal.course;
    try {
      setEnrollmentLoading(true);

      // Prevent enrolling if already enrolled
      if (enrolledCourseIds.has(course._id)) {
        toast('You are already enrolled in this course');
        setShowEnrollmentModal({ show: false, course: null });
        return;
      }

      // Zero-fee fallback
      if (!course.fees || course.fees === 0) {
        await axios.post('/api/enrollments', { courseId: course._id });
        toast.success('Successfully enrolled in course!');
        fetchCourses();
        fetchStudentEnrollments();
        setShowEnrollmentModal({ show: false, course: null });
        return;
      }

      // Create Razorpay order with wallet option
      const orderRes = await axios.post('/api/enrollments/create-order', {
        courseId: course._id,
        useWallet: useWallet
      });
      if (!orderRes.data || !orderRes.data.orderId) {
        toast.error(orderRes.data?.message || 'Unable to create payment order');
        return;
      }

      const { orderId, amount, currency, key, walletAmountUsed, finalAmount } = orderRes.data;

      // If wallet covers the full amount, no payment needed
      if (amount === 0) {
        setShowEnrollmentModal({ show: false, course: null });
        toast.success('Enrolled successfully using wallet!');
        fetchCourses();
        fetchStudentEnrollments();
        return;
      }

      // Load Razorpay SDK
      const loadRazorpay = () => new Promise((resolve, reject) => {
        if (window.Razorpay) return resolve(window.Razorpay);
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => resolve(window.Razorpay);
        script.onerror = () => reject(new Error('Failed to load Razorpay SDK'));
        document.body.appendChild(script);
      });

      await loadRazorpay();

      const options = {
        key,
        amount,
        currency,
        name: course.title,
        description: `Enroll: ${course.courseCode} (₹${walletAmountUsed > 0 ? walletAmountUsed + ' from wallet + ' + finalAmount + ' to pay' : course.fees})`,
        order_id: orderId,
        handler: async function (response) {
          try {
            await axios.post('/api/enrollments/verify-payment', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              courseId: course._id
            });
            toast.success('Payment successful & enrolled!');
            fetchCourses();
            fetchStudentEnrollments();
            setShowEnrollmentModal({ show: false, course: null });
          } catch (err) {
            console.error('Verify payment error', err);
            toast.error('Payment processed but verification failed. Enrollment will be updated shortly.');
          }
        },
        modal: {
          ondismiss: function () {
            toast('Payment cancelled. You can try again anytime.');
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (error) {
      console.error('Enrollment error:', error);
      const message = error.response?.data?.message || 'Failed to enroll';
      toast.error(message);
    } finally {
      setEnrollmentLoading(false);
    }
  };

  const handleApproveCourse = async (courseId) => {
    try {
      await axios.put(`/api/courses/${courseId}/approve`);
      toast.success('Course approved successfully!');
      fetchCourses();
    } catch (error) {
      toast.error('Failed to approve course');
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;
    try {
      await axios.delete(`/api/courses/${courseId}`);
      toast.success('Course deleted successfully!');
      fetchCourses();
    } catch (error) {
      toast.error('Failed to delete course');
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedLevel('');
  };

  if (loading && courses.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Courses</h1>
          <p className="mt-2 text-gray-600">
            Discover and enroll in courses that match your interests
          </p>
        </div>

        {user?.role === 'instructor' || user?.role === 'admin' ? (
          <Link
            to="/create-course"
            className="mt-4 sm:mt-0 btn btn-primary flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Create Course
          </Link>
        ) : null}
      </div>

      {/* Search and Filters */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search courses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10"
            />
          </div>

          {/* Category Filter */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="input"
          >
            <option value="">All Categories</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>

          {/* Level Filter */}
          <select
            value={selectedLevel}
            onChange={(e) => setSelectedLevel(e.target.value)}
            className="input"
          >
            <option value="">All Levels</option>
            {levels.map((level) => (
              <option key={level} value={level}>
                {level}
              </option>
            ))}
          </select>

          {/* Clear Filters */}
          <button
            onClick={clearFilters}
            className="btn btn-secondary flex items-center justify-center"
          >
            <FunnelIcon className="h-5 w-5 mr-2" />
            Clear Filters
          </button>
        </div>
      </div>

      {/* Course Grid */}
      {courses.length === 0 ? (
        <div className="text-center py-12">
          <BookOpenIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No courses found</h3>
          <p className="text-gray-600">Try adjusting your search criteria</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((course) => (
            <div key={course._id} className="card hover:shadow-md transition-shadow duration-200">
              {/* Course Image */}
              <div className="h-48 rounded-lg mb-4 overflow-hidden border">
                {course.banner ? (
                  <img
                    src={course.banner}
                    alt={course.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="h-full w-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                    <BookOpenIcon className="h-16 w-16 text-white" />
                  </div>
                )}
              </div>

              {/* Course Info */}
              <div className="space-y-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                    {course.title}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">{course.courseCode}</p>
                  {!course.isApproved && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 mt-1">
                      Pending Approval
                    </span>
                  )}
                </div>

                <p className="text-gray-600 text-sm line-clamp-3">
                  {course.description}
                </p>

                {/* Course Details */}
                <div className="space-y-2 text-sm text-gray-600">
                  {user?.role !== 'student' && (
                    <div className="flex items-center">
                      <UserIcon className="h-4 w-4 mr-2" />
                      {course.instructor?.firstName} {course.instructor?.lastName}
                    </div>
                  )}

                  {/* <div className="flex items-center">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    {course.credits} credits • {course.level}
                  </div> */}

                  <div className="flex items-center">
                    <ClockIcon className="h-4 w-4 mr-2" />
                    {formatDuration(course.totalDuration || 0)}
                  </div>
                  <div className="flex items-center">
                    <CurrencyRupeeIcon className="h-4 w-4 mr-2" />
                    {course.fees}
                  </div>
                </div>

                {/* Enrollment Status */}
                <div className="flex items-center justify-between text-sm">
                  {user?.role !== 'student' && (
                    <span className="text-gray-600">
                      {course.currentEnrollment}/{course.maxStudents} enrolled
                    </span>
                  )}
                  <span
                    className={`px-2 py-1 rounded-full text-xs ${course.level === 'Beginner'
                      ? 'bg-green-100 text-green-800'
                      : course.level === 'Intermediate'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                      }`}
                  >
                    {course.level}
                  </span>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-2 pt-4 flex-wrap">
                  <Link
                    to={`/courses/${course._id}`}
                    className="flex-1 btn btn-secondary text-center flex items-center justify-center"
                  >
                    View Details
                  </Link>

                  {user?.role === 'student' && course.isApproved && !enrolledCourseIds.has(course._id) && (
                    <button
                      onClick={() => handleEnrollClick(course)}
                      disabled={course.currentEnrollment >= course.maxStudents || enrollmentLoading}
                      className="flex-1 btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {enrollmentLoading ? 'Processing...' : course.currentEnrollment >= course.maxStudents ? 'Full' : 'Enroll'}
                    </button>
                  )}

                  {user?.role === 'student' && !course.isApproved && (
                    <button
                      disabled
                      className="flex-1 btn btn-secondary opacity-50 cursor-not-allowed"
                    >
                      Pending Approval
                    </button>
                  )}

                  {user?.role === 'admin' && !course.isApproved && (
                    <button
                      onClick={() => handleApproveCourse(course._id)}
                      className="flex-1 btn btn-primary"
                    >
                      Approve
                    </button>
                  )}

                  {user?.role === 'admin' || user?.role === 'instructor' ? (
                    <>
                      <Link
                        to={`/edit-course/${course._id}`}
                        className="btn btn-secondary flex items-center"
                      >
                        <PencilSquareIcon className="h-4 w-4 mr-1" />
                        Edit
                      </Link>
                      {/* <button
                        onClick={() => handleDeleteCourse(course._id)}
                        className="btn btn-danger flex items-center"
                      >
                        <TrashIcon className="h-4 w-4 mr-1" />
                        Delete
                      </button> */}
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex justify-center space-x-2">
          {pagination.hasPrev && (
            <button
              onClick={() => fetchCourses(pagination.current - 1)}
              className="btn btn-secondary"
            >
              Previous
            </button>
          )}

          <span className="flex items-center px-4 py-2 text-sm text-gray-600">
            Page {pagination.current} of {pagination.pages}
          </span>

          {pagination.hasNext && (
            <button
              onClick={() => fetchCourses(pagination.current + 1)}
              className="btn btn-secondary"
            >
              Next
            </button>
          )}
        </div>
      )}

      {/* Enrollment Modal */}
      {showEnrollmentModal.show && showEnrollmentModal.course && (
        <EnrollmentModal
          course={showEnrollmentModal.course}
          user={user}
          onEnroll={handleEnroll}
          onClose={() => setShowEnrollmentModal({ show: false, course: null })}
          enrollmentLoading={enrollmentLoading}
        />
      )}
    </div>
  );
};

export default CourseList;
